import { gql } from "graphql-request";

// ─── Queries ────────────────────────────────────────────────────────

export const GET_FIRST_SALE_SUPPLIERS = gql`
  query FirstSaleSuppliers($tenantId: ID, $clientId: ID, $status: FirstSaleSupplierStatus, $search: String) {
    firstSaleSuppliers(tenantId: $tenantId, clientId: $clientId, status: $status, search: $search) {
      _id
      tenantId
      clientId
      mid
      supplierName
      country
      factoryName
      factoryCountry
      status
      casesCount
      activeCases
      totalSavings
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_SUPPLIER = gql`
  query FirstSaleSupplier($supplierId: ID!, $tenantId: ID) {
    firstSaleSupplier(supplierId: $supplierId, tenantId: $tenantId) {
      _id
      tenantId
      clientId
      mid
      supplierName
      country
      factoryName
      factoryCountry
      status
      casesCount
      activeCases
      totalSavings
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_CASES = gql`
  query FirstSaleCases($tenantId: ID, $clientId: ID, $supplierId: ID, $status: FirstSaleCaseStatus) {
    firstSaleCases(tenantId: $tenantId, clientId: $clientId, supplierId: $supplierId, status: $status) {
      _id
      tenantId
      clientId
      supplierId
      caseNumber
      poNumber
      linkedPOs
      status
      middlemanName
      factoryName
      middlemanValue
      factoryValue
      savings
      dutyRate
      htsCode
      productDescription
      multipleMiddlemen
      backToBackFOB
      materialsProvided
      relatedParties
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_CASE = gql`
  query FirstSaleCase($caseId: ID!, $tenantId: ID) {
    firstSaleCase(caseId: $caseId, tenantId: $tenantId) {
      _id
      tenantId
      clientId
      supplierId
      caseNumber
      poNumber
      linkedPOs
      status
      middlemanName
      factoryName
      middlemanValue
      factoryValue
      savings
      dutyRate
      htsCode
      productDescription
      multipleMiddlemen
      backToBackFOB
      materialsProvided
      relatedParties
      portal2faEnabled
      portalAuthorizedEmails
      portalToken
      portalEnabled
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_DOCUMENTS = gql`
  query FirstSaleDocuments($tenantId: ID, $caseId: ID, $supplierId: ID, $category: FirstSaleDocumentCategory) {
    firstSaleDocuments(tenantId: $tenantId, caseId: $caseId, supplierId: $supplierId, category: $category) {
      _id
      tenantId
      clientId
      caseId
      supplierId
      category
      slotId
      s3Key
      filename
      mimeType
      sizeBytes
      status
      scope
      annotation
      revisionRequestId
      revisionReason
      isEnglish
      detectedLanguage
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_DOCUMENT_URL = gql`
  query FirstSaleDocumentUrl($docId: ID!, $tenantId: ID) {
    firstSaleDocumentUrl(docId: $docId, tenantId: $tenantId) {
      document {
        _id
        filename
        mimeType
        s3Key
      }
      downloadUrl
    }
  }
`;

export const GET_FIRST_SALE_VALIDATION = gql`
  query FirstSaleValidation($caseId: ID!, $tenantId: ID) {
    firstSaleValidation(caseId: $caseId, tenantId: $tenantId) {
      _id
      caseId
      tenantId
      configVersion
      overallScore
      requirements {
        checkId
        checkName
        category
        status
        detail
        reasoning
        citations {
          documentId
          documentName
          excerpt
          pageNumber
        }
        items {
          itemId
          status
          detail
        }
        reviewStatus
        reviewedBy
        reviewedAt
        reviewNote
      }
      crossDocValidation {
        checkId
        checkName
        status
        detail
        reasoning
        linkedDocuments {
          documentId
          documentName
        }
        citations {
          documentId
          documentName
          excerpt
          pageNumber
        }
        reviewStatus
        reviewedBy
        reviewedAt
        reviewNote
      }
      processingStatus
      totalChecks
      completedChecks
      signedOff
      signedOffBy
      signedOffAt
      signOffNotes
      signOffApproved
      modelUsed
      processingTimeMs
      createdAt
      updatedAt
    }
  }
`;

export const GET_FIRST_SALE_DOCUMENT_SECTIONS = gql`
  query FirstSaleDocumentSections($multipleMiddlemen: Boolean, $backToBackFOB: Boolean, $materialsProvided: Boolean, $relatedParties: Boolean, $factorySellsToUnrelated: Boolean, $middlemanBuysFromUnrelated: Boolean) {
    firstSaleDocumentSections(multipleMiddlemen: $multipleMiddlemen, backToBackFOB: $backToBackFOB, materialsProvided: $materialsProvided, relatedParties: $relatedParties, factorySellsToUnrelated: $factorySellsToUnrelated, middlemanBuysFromUnrelated: $middlemanBuysFromUnrelated) {
      sectionId
      label
      description
      slots {
        slotId
        label
        description
        required
        allowMultiple
        acceptedMimeTypes
        scope
        condition
      }
    }
  }
`;

export const GET_FIRST_SALE_SUPPLIER_DOCUMENTS = gql`
  query FirstSaleSupplierDocuments($supplierId: ID!, $tenantId: ID) {
    firstSaleSupplierDocuments(supplierId: $supplierId, tenantId: $tenantId) {
      _id
      tenantId
      clientId
      supplierId
      scope
      category
      slotId
      s3Key
      filename
      mimeType
      sizeBytes
      status
      annotation
      createdAt
      updatedAt
    }
  }
`;

// ─── Mutations ──────────────────────────────────────────────────────

export const CREATE_FIRST_SALE_SUPPLIER = gql`
  mutation CreateFirstSaleSupplier($input: CreateFirstSaleSupplierInput!) {
    createFirstSaleSupplier(input: $input) {
      _id
      tenantId
      clientId
      mid
      supplierName
      country
      factoryName
      factoryCountry
      status
      createdAt
    }
  }
`;

export const UPDATE_FIRST_SALE_SUPPLIER = gql`
  mutation UpdateFirstSaleSupplier($supplierId: ID!, $tenantId: ID, $input: UpdateFirstSaleSupplierInput!) {
    updateFirstSaleSupplier(supplierId: $supplierId, tenantId: $tenantId, input: $input) {
      _id
      supplierName
      status
      updatedAt
    }
  }
`;

export const CREATE_FIRST_SALE_CASE = gql`
  mutation CreateFirstSaleCase($input: CreateFirstSaleCaseInput!) {
    createFirstSaleCase(input: $input) {
      _id
      tenantId
      supplierId
      caseNumber
      poNumber
      status
      middlemanName
      factoryName
      middlemanValue
      factoryValue
      savings
      dutyRate
      htsCode
      productDescription
      createdAt
    }
  }
`;

export const UPDATE_FIRST_SALE_CASE = gql`
  mutation UpdateFirstSaleCase($caseId: ID!, $tenantId: ID, $input: UpdateFirstSaleCaseInput!) {
    updateFirstSaleCase(caseId: $caseId, tenantId: $tenantId, input: $input) {
      _id
      status
      middlemanValue
      factoryValue
      savings
      dutyRate
      multipleMiddlemen
      backToBackFOB
      materialsProvided
      relatedParties
      updatedAt
    }
  }
`;

export const UPLOAD_FIRST_SALE_DOCUMENT = gql`
  mutation UploadFirstSaleDocument($input: UploadFirstSaleDocumentInput!) {
    uploadFirstSaleDocument(input: $input) {
      docId
      uploadUrl
      s3Key
    }
  }
`;

export const UPLOAD_FIRST_SALE_SUPPLIER_DOCUMENT = gql`
  mutation UploadFirstSaleSupplierDocument($input: UploadFirstSaleDocumentInput!) {
    uploadFirstSaleSupplierDocument(input: $input) {
      docId
      uploadUrl
      s3Key
    }
  }
`;

export const CONFIRM_FIRST_SALE_DOCUMENT_UPLOAD = gql`
  mutation ConfirmFirstSaleDocumentUpload($docId: ID!, $tenantId: ID) {
    confirmFirstSaleDocumentUpload(docId: $docId, tenantId: $tenantId) {
      _id
      isEnglish
      detectedLanguage
    }
  }
`;

export const DELETE_FIRST_SALE_DOCUMENT = gql`
  mutation DeleteFirstSaleDocument($docId: ID!, $tenantId: ID) {
    deleteFirstSaleDocument(docId: $docId, tenantId: $tenantId)
  }
`;

export const DELETE_FIRST_SALE_CASE = gql`
  mutation DeleteFirstSaleCase($caseId: ID!, $tenantId: ID) {
    deleteFirstSaleCase(caseId: $caseId, tenantId: $tenantId)
  }
`;

export const DELETE_FIRST_SALE_SUPPLIER = gql`
  mutation DeleteFirstSaleSupplier($supplierId: ID!, $tenantId: ID) {
    deleteFirstSaleSupplier(supplierId: $supplierId, tenantId: $tenantId)
  }
`;

export const RUN_FIRST_SALE_VALIDATION = gql`
  mutation RunFirstSaleValidation($caseId: ID!, $tenantId: ID) {
    runFirstSaleValidation(caseId: $caseId, tenantId: $tenantId) {
      _id
      caseId
      processingStatus
      totalChecks
      completedChecks
    }
  }
`;

export const RERUN_FIRST_SALE_VALIDATION_CHECK = gql`
  mutation RerunFirstSaleValidationCheck($caseId: ID!, $checkId: String!, $tenantId: ID) {
    rerunFirstSaleValidationCheck(caseId: $caseId, checkId: $checkId, tenantId: $tenantId) {
      _id
      caseId
      overallScore
      requirements {
        checkId
        checkName
        category
        status
        detail
        reasoning
        citations {
          documentId
          documentName
          excerpt
          pageNumber
        }
        items {
          itemId
          status
          detail
        }
        reviewStatus
      }
      crossDocValidation {
        checkId
        checkName
        status
        detail
        reasoning
        citations {
          documentId
          documentName
          excerpt
          pageNumber
        }
        reviewStatus
      }
    }
  }
`;

export const UPDATE_VALIDATION_CHECK_REVIEW = gql`
  mutation UpdateValidationCheckReview($caseId: ID!, $checkId: String!, $tenantId: ID, $input: UpdateValidationCheckReviewInput!) {
    updateValidationCheckReview(caseId: $caseId, checkId: $checkId, tenantId: $tenantId, input: $input) {
      _id
      requirements {
        checkId
        reviewStatus
        reviewedBy
        reviewedAt
        reviewNote
      }
      crossDocValidation {
        checkId
        reviewStatus
        reviewedBy
        reviewedAt
        reviewNote
      }
    }
  }
`;

export const SIGN_OFF_VALIDATION = gql`
  mutation SignOffValidation($caseId: ID!, $tenantId: ID, $approved: Boolean!, $notes: String) {
    signOffValidation(caseId: $caseId, tenantId: $tenantId, approved: $approved, notes: $notes) {
      _id
      signedOff
      signedOffBy
      signedOffAt
      signOffNotes
      signOffApproved
    }
  }
`;

// ─── Data Extraction ────────────────────────────────────────────────

export const GET_FIRST_SALE_DATA_EXTRACTION = gql`
  query FirstSaleDataExtraction($caseId: ID!, $tenantId: ID) {
    firstSaleDataExtraction(caseId: $caseId, tenantId: $tenantId) {
      _id
      caseId
      tenantId
      status
      components {
        componentId
        supplierName
        materialType
        materialDescription
        quantity
        quantityUnit
        perUnitCostUSD
        appliesTo
        invoiceNumber
        invoiceNetAmount
        invoiceGrossAmount
        invoiceCurrency
        invoiceDocumentId
        paymentDate
        paymentAmount
        paymentDocumentId
        confidence
        matchNotes
        notes
        userStatus
      }
      summary {
        materialsTotal
        sewingCost
        assistOverhead
        transportCost
        firstSalePrice
        currency
        totalUnits
        secondSalePrice
        firstSaleTotal
        secondSaleTotal
        dutySavings
      }
      extractedAt
      modelUsed
      errorMessage
      createdAt
      updatedAt
    }
  }
`;

export const RUN_FIRST_SALE_DATA_EXTRACTION = gql`
  mutation RunFirstSaleDataExtraction($caseId: ID!, $tenantId: ID) {
    runFirstSaleDataExtraction(caseId: $caseId, tenantId: $tenantId) {
      _id
      status
    }
  }
`;

export const UPDATE_DATA_EXTRACTION_COMPONENT = gql`
  mutation UpdateDataExtractionComponent($caseId: ID!, $componentId: String!, $input: UpdateDataExtractionComponentInput!, $tenantId: ID) {
    updateDataExtractionComponent(caseId: $caseId, componentId: $componentId, input: $input, tenantId: $tenantId) {
      _id
      components {
        componentId
        supplierName
        materialType
        materialDescription
        quantity
        quantityUnit
        perUnitCostUSD
        appliesTo
        invoiceNumber
        invoiceNetAmount
        invoiceGrossAmount
        invoiceCurrency
        invoiceDocumentId
        paymentDate
        paymentAmount
        paymentDocumentId
        confidence
        matchNotes
        notes
        userStatus
      }
    }
  }
`;

export const ADD_DATA_EXTRACTION_COMPONENT = gql`
  mutation AddDataExtractionComponent($caseId: ID!, $input: AddDataExtractionComponentInput!, $tenantId: ID) {
    addDataExtractionComponent(caseId: $caseId, input: $input, tenantId: $tenantId) {
      _id
      components {
        componentId
        supplierName
        materialType
        materialDescription
        quantity
        perUnitCostUSD
        userStatus
      }
    }
  }
`;

export const REMOVE_DATA_EXTRACTION_COMPONENT = gql`
  mutation RemoveDataExtractionComponent($caseId: ID!, $componentId: String!, $tenantId: ID) {
    removeDataExtractionComponent(caseId: $caseId, componentId: $componentId, tenantId: $tenantId) {
      _id
      components {
        componentId
      }
    }
  }
`;

// ─── Activity Events ────────────────────────────────────────────────

export const GET_FIRST_SALE_ACTIVITY_EVENTS = gql`
  query FirstSaleActivityEvents($caseId: ID!, $tenantId: ID) {
    firstSaleActivityEvents(caseId: $caseId, tenantId: $tenantId) {
      _id
      tenantId
      caseId
      type
      title
      description
      user
      userId
      createdAt
    }
  }
`;

export const CREATE_FIRST_SALE_ACTIVITY_EVENT = gql`
  mutation CreateFirstSaleActivityEvent(
    $caseId: ID!
    $tenantId: ID
    $type: FirstSaleActivityEventType!
    $title: String!
    $description: String!
  ) {
    createFirstSaleActivityEvent(
      caseId: $caseId
      tenantId: $tenantId
      type: $type
      title: $title
      description: $description
    ) {
      _id
      type
      title
      description
      user
      createdAt
    }
  }
`;

// ─── Memo & Letter Generation ──────────────────────────────────────

export const GENERATE_FIRST_SALE_MEMO = gql`
  mutation GenerateFirstSaleMemo($caseId: ID!, $tenantId: ID, $attorney: String) {
    generateFirstSaleMemo(caseId: $caseId, tenantId: $tenantId, attorney: $attorney) {
      _id
      type
      title
      content
      generatedBy
      promptTokens
      completionTokens
      createdAt
    }
  }
`;

export const GENERATE_FIRST_SALE_NOTIFICATION_LETTER = gql`
  mutation GenerateFirstSaleNotificationLetter(
    $caseId: ID!
    $tenantId: ID
    $attorney: String
    $recipientName: String
    $recipientTitle: String
    $importerAddress: String
    $iorNumber: String
  ) {
    generateFirstSaleNotificationLetter(
      caseId: $caseId
      tenantId: $tenantId
      attorney: $attorney
      recipientName: $recipientName
      recipientTitle: $recipientTitle
      importerAddress: $importerAddress
      iorNumber: $iorNumber
    ) {
      _id
      type
      title
      content
      generatedBy
      promptTokens
      completionTokens
      createdAt
    }
  }
`;

export const GET_FIRST_SALE_GENERATED_DOCS = gql`
  query FirstSaleGeneratedDocs($caseId: ID!, $tenantId: ID) {
    firstSaleGeneratedDocs(caseId: $caseId, tenantId: $tenantId) {
      _id
      type
      title
      content
      generatedBy
      promptTokens
      completionTokens
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_FIRST_SALE_GENERATED_DOC = gql`
  mutation UpdateFirstSaleGeneratedDoc($docId: ID!, $tenantId: ID, $title: String, $content: String) {
    updateFirstSaleGeneratedDoc(docId: $docId, tenantId: $tenantId, title: $title, content: $content) {
      _id
      title
      content
      updatedAt
    }
  }
`;

export const SAVE_FIRST_SALE_GENERATED_DOC = gql`
  mutation SaveFirstSaleGeneratedDoc(
    $caseId: ID!
    $tenantId: ID
    $type: String!
    $title: String!
    $content: String!
  ) {
    saveFirstSaleGeneratedDoc(
      caseId: $caseId
      tenantId: $tenantId
      type: $type
      title: $title
      content: $content
    ) {
      _id
      type
      title
      content
      generatedBy
      createdAt
    }
  }
`;

export const DELETE_FIRST_SALE_GENERATED_DOC = gql`
  mutation DeleteFirstSaleGeneratedDoc($docId: ID!, $tenantId: ID) {
    deleteFirstSaleGeneratedDoc(docId: $docId, tenantId: $tenantId)
  }
`;

// ─── Document Requests ──────────────────────────────────────────────

export const GET_FIRST_SALE_DOCUMENT_REQUESTS = gql`
  query FirstSaleDocumentRequests($caseId: ID!, $tenantId: ID) {
    firstSaleDocumentRequests(caseId: $caseId, tenantId: $tenantId) {
      _id
      caseId
      slotId
      label
      description
      reason
      requirements
      linkedIssues {
        itemId
        status
        detail
      }
      status
      requestedBy
      fulfilledAt
      createdAt
    }
  }
`;

export const CREATE_FIRST_SALE_DOCUMENT_REQUESTS = gql`
  mutation CreateFirstSaleDocumentRequests(
    $caseId: ID!
    $tenantId: ID
    $requests: [FirstSaleDocumentRequestInput!]!
    $notifyEmail: String
  ) {
    createFirstSaleDocumentRequests(
      caseId: $caseId
      tenantId: $tenantId
      requests: $requests
      notifyEmail: $notifyEmail
    ) {
      _id
      slotId
      label
      reason
      requirements
      linkedIssues {
        itemId
        status
        detail
      }
      status
      createdAt
    }
  }
`;

export const FULFILL_FIRST_SALE_DOCUMENT_REQUEST = gql`
  mutation FulfillFirstSaleDocumentRequest($requestId: ID!, $tenantId: ID) {
    fulfillFirstSaleDocumentRequest(requestId: $requestId, tenantId: $tenantId) {
      _id
      status
      fulfilledAt
    }
  }
`;

export const UPDATE_FIRST_SALE_DOCUMENT_REQUEST = gql`
  mutation UpdateFirstSaleDocumentRequest(
    $requestId: ID!
    $tenantId: ID
    $input: UpdateFirstSaleDocumentRequestInput!
  ) {
    updateFirstSaleDocumentRequest(requestId: $requestId, tenantId: $tenantId, input: $input) {
      _id
      slotId
      label
      reason
      requirements
      linkedIssues {
        itemId
        status
        detail
      }
      status
      createdAt
    }
  }
`;

export const DELETE_FIRST_SALE_DOCUMENT_REQUEST = gql`
  mutation DeleteFirstSaleDocumentRequest($requestId: ID!, $tenantId: ID) {
    deleteFirstSaleDocumentRequest(requestId: $requestId, tenantId: $tenantId)
  }
`;

// ─── Case Questions (internal view) ─────────────────────────────────

export const GET_FIRST_SALE_CASE_QUESTIONS = gql`
  query FirstSaleCaseQuestions($caseId: ID!, $tenantId: ID) {
    firstSaleCaseQuestions(caseId: $caseId, tenantId: $tenantId) {
      _id
      questionId
      question
      category
      options
      answer
      status
    }
  }
`;
